/**
 * Object screen data: overview, history download and statistics, VPP
 * potential and application, the day's plan and actual battery work, the
 * day's 6 Deye windows, the internal-arbitrage switch. Owner or
 * administrator (switch and application: owner).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DeyeWindow,
  SiteAnalysisResponse,
  SiteApplicationResponse,
  SiteDayResponse,
  SiteMoneyResponse,
  SiteOverviewResponse,
  SitePotentialResponse,
  SiteWindowsResponse,
} from 'shared';

import { api } from '../lib/api-client.js';

const base = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}`;

export function useSiteOverview(siteId: string) {
  return useQuery({
    queryKey: ['site-overview', siteId],
    queryFn: () => api.get<SiteOverviewResponse>(`${base(siteId)}/overview`),
    enabled: !!siteId,
    refetchInterval: 60_000,
  });
}

/** Polls every 10 s while the download runs, otherwise every 5 min. */
export function useSiteAnalysis(siteId: string) {
  return useQuery({
    queryKey: ['site-analysis', siteId],
    queryFn: () => api.get<SiteAnalysisResponse>(`${base(siteId)}/analysis`),
    enabled: !!siteId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pending' || s === 'running' ? 10_000 : 5 * 60_000;
    },
  });
}

/**
 * D11 «Гроші»: the same 30 complete days as the statistics — they change
 * once a day; the API keeps a result 30 minutes. Saving settings or rates
 * refreshes it at once (`use-sites.ts`, `use-tariff.ts`).
 */
export function useSiteMoney(siteId: string) {
  return useQuery({
    queryKey: ['site-money', siteId],
    queryFn: () => api.get<SiteMoneyResponse>(`${base(siteId)}/money`),
    enabled: !!siteId,
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
  });
}

/** «Повторити зараз»: the administrator's restart is journalled (`/admin/sites/:id/analysis`). */
export function useRestartAnalysis(siteId: string, asAdmin: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ queued: boolean }>(
        asAdmin ? `/api/admin/sites/${encodeURIComponent(siteId)}/analysis` : `${base(siteId)}/analysis`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['site-analysis', siteId] }),
  });
}

export function useSitePotential(siteId: string, downloading: boolean) {
  return useQuery({
    queryKey: ['site-potential', siteId],
    queryFn: () => api.get<SitePotentialResponse>(`${base(siteId)}/potential`).then((r) => r.potential),
    enabled: !!siteId,
    refetchInterval: downloading ? 15_000 : 5 * 60_000,
  });
}

/** The plan and actual of local day `date` (today: a new query at midnight). */
export function useSiteDay(siteId: string, date: string) {
  return useQuery({
    queryKey: ['site-day', siteId, date],
    queryFn: () => api.get<SiteDayResponse>(`${base(siteId)}/day?date=${encodeURIComponent(date)}`),
    enabled: !!siteId && !!date,
    refetchInterval: 5 * 60_000,
  });
}

export function useSetInternalArbitrage(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch<{ internal_arbitrage_enabled: boolean }>(`${base(siteId)}/internal-arbitrage`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['site-overview', siteId] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      // The plan is stored in the background within seconds, then written to
      // the inverter: refresh the day shortly after, and once more later.
      for (const ms of [3_000, 15_000]) {
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ['site-day', siteId] });
          void qc.invalidateQueries({ queryKey: ['site-windows', siteId] });
        }, ms);
      }
    },
  });
}

export function useSiteWindows(siteId: string) {
  return useQuery({
    queryKey: ['site-windows', siteId],
    queryFn: () => api.get<SiteWindowsResponse>(`${base(siteId)}/windows`),
    enabled: !!siteId,
    refetchInterval: 5 * 60_000,
  });
}

/** Manual edit of today's windows, kept to the end of the day and written to the inverter. */
export function useSaveWindows(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; windows: DeyeWindow[] }) =>
      api.put<SiteWindowsResponse>(`${base(siteId)}/windows`, body),
    onSuccess: (r) => {
      qc.setQueryData(['site-windows', siteId], r);
      void qc.invalidateQueries({ queryKey: ['site-day', siteId] });
      void qc.invalidateQueries({ queryKey: ['site-overview', siteId] });
    },
  });
}

/** «Повернути автоматичний»: answers the re-planned windows. */
export function useResetWindows(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<SiteWindowsResponse>(`${base(siteId)}/windows/manual`),
    onSuccess: (r) => {
      qc.setQueryData(['site-windows', siteId], r);
      void qc.invalidateQueries({ queryKey: ['site-day', siteId] });
      void qc.invalidateQueries({ queryKey: ['site-overview', siteId] });
    },
  });
}

export function useApplication(siteId: string) {
  return useQuery({
    queryKey: ['site-application', siteId],
    queryFn: () => api.get<SiteApplicationResponse>(`${base(siteId)}/application`).then((r) => r.application),
    enabled: !!siteId,
  });
}

/** «Подати заявку на підключення до VPP» (owner). */
export function useApply(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<SiteApplicationResponse>(`${base(siteId)}/application`).then((r) => r.application),
    onSuccess: (application) => qc.setQueryData(['site-application', siteId], application),
  });
}
